import React, { useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import type { BillingRecord } from "@services/billing";
import CancelImpactSection from "./CancelImpactSection";

type ReviewSubscriptionDialogProps = {
  open: boolean;
  record: BillingRecord | null;
  facilityId?: number | null;
  accessEndDate?: string | null;
  onKeep: () => void;
  onContinueToCancel: () => void;
};

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slash) {
    return new Date(Number(slash[3]), Number(slash[1]) - 1, Number(slash[2]));
  }
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatShortDate(value: string | null | undefined): string {
  const d = parseDate(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

export const ReviewSubscriptionDialog: React.FC<
  ReviewSubscriptionDialogProps
> = ({
  open,
  record,
  facilityId,
  accessEndDate,
  onKeep,
  onContinueToCancel,
}) => {
  const plan = record?.selectedPlan || "Plan";
  const frequency = record?.billingFrequency || "Monthly";
  const periodStart = formatShortDate(
    record?.planStartDate || record?.invoiceDate,
  );
  const periodEnd = formatShortDate(
    record?.planEndDate || record?.planEndDateIso || record?.expiryDate,
  );
  const archiveUntil = useMemo(() => {
    const end = parseDate(
      record?.planEndDate || record?.planEndDateIso || record?.expiryDate,
    );
    if (!end) return "—";
    return addMonths(end, 6).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [record]);

  const archiveMessage = `Compliance data will be securely archived for 6 months after removal (until ${archiveUntil}). You can resubscribe at any time during that period to restore access.`;

  return (
    <Dialog
      open={open}
      onClose={onKeep}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          fontFamily: '"Rethink Sans", sans-serif',
          maxHeight: "92vh",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: { xs: 2.5, sm: 3 },
          pt: 2.5,
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionOutlinedIcon sx={{ color: "#0a829d", fontSize: 22 }} />
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#065b70",
              lineHeight: 1.3,
            }}
          >
            Review subscription
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onKeep}
          aria-label="Close"
          sx={{ color: "#737373" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3 },
          pt: 0,
          pb: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#525252", lineHeight: 1.5 }}>
          Review the details below and understand what will be affected before
          proceeding.
        </Typography>

        <Box
          sx={{
            backgroundColor: "#f3fdff",
            border: "1px solid #d8eff4",
            borderRadius: "10px",
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <BusinessOutlinedIcon sx={{ color: "#0a829d", fontSize: 20 }} />
            <Typography
              sx={{ fontSize: "15px", fontWeight: 700, color: "#065b70" }}
            >
              {record?.facilityName || "Facility"}
            </Typography>
          </Box>
          <Typography
            sx={{ fontSize: "13px", color: "#737373", mb: 2, pl: 3.5 }}
          >
            {plan} · {frequency} billing
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            {[
              { label: "Plan", value: plan },
              { label: "Billing frequency", value: frequency },
              { label: "Period started", value: periodStart },
              { label: "Period ends", value: periodEnd },
            ].map((item) => (
              <Box key={item.label}>
                <Typography
                  sx={{ fontSize: "12px", color: "#737373", mb: 0.5 }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 600, color: "#262626" }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
            backgroundColor: "#fff8f0",
            border: "1px solid #f0c48a",
            borderRadius: "8px",
            px: 1.75,
            py: 1.5,
          }}
        >
          <WarningAmberRoundedIcon
            sx={{ color: "#e65100", fontSize: 20, mt: "1px", flexShrink: 0 }}
          />
          <Typography sx={{ fontSize: "13px", color: "#5d4037", lineHeight: 1.45 }}>
            {archiveMessage}
          </Typography>
        </Box>

        {facilityId != null && accessEndDate ? (
          <CancelImpactSection
            facilityId={facilityId}
            accessEndDate={accessEndDate}
            showArchiveBanner={false}
          />
        ) : null}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "space-between",
            pt: 0.5,
          }}
        >
          <Button
            type="button"
            onClick={onKeep}
            startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "14px",
              color: "#e6fafc",
              background: "linear-gradient(to right, #0a829d, #58c2d8)",
              borderRadius: "8px",
              px: 3,
              py: 1.1,
              flex: { xs: "1 1 100%", sm: "0 1 auto" },
              "&:hover": {
                background: "linear-gradient(to right, #08708a, #4ab0c6)",
              },
            }}
          >
            Keep Subscription
          </Button>
          <Button
            type="button"
            variant="outlined"
            onClick={onContinueToCancel}
            startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "14px",
              color: "#404040",
              borderColor: "#b3b3b3",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              px: 3,
              py: 1.1,
              flex: { xs: "1 1 100%", sm: "0 1 auto" },
              "&:hover": {
                borderColor: "#8a8a8a",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Continue to Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewSubscriptionDialog;
